import React from "react";
import "./timetable.scss";
import Loader from "../../common/components/shared/loader/loader";
import {
    TimetableDayWithRowsModel,
    TimetableModel,
    SimpleTimetableActionModel, WeekAndYearModel
} from "../models/timetable";
import {TimetableService} from "../services/timetableService";
import {DateUtils} from "../../helpers/dateUtils";
import Tile from "./tile/tile";
import i18n from "../../i18n/i18n";
import {NavLink, withRouter} from "react-router-dom";
import {routingAddresses} from "../../helpers/routing";
import Button from "../../common/components/shared/button/button";
import {InputLabel} from "@mui/material";
import DropDown from "../../common/components/shared/dropDown/dropDown";
import {ValidatedValue} from "../../helpers/validatedValue";
import {Utils} from "../../helpers/utils";
import {Api} from "../../helpers/api";
import DownloadPopup from "./downloadPopup/downloadPopup";
import CopyWeekPopup from "./copySelectedWeekPopup/copyWeekPopup";
import {ReactComponent as CopyWeekIcon} from "../../statics/icons/icon_copy.svg";
import {ReactComponent as DownloadIcon} from "../../statics/icons/icon_print.svg";
import {DropDownModel} from "../../common/models/dropDown";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../common/models/httpError";
import Row from "../../common/components/shared/row/row";
import {HttpError} from "../../common/components/shared/httpError/httpError";
import {storesContext} from "../../common/stores/stores";
import {ITimetableStore} from "../stores/timetableStore";
import ReloadButton from "../../common/components/shared/reloadButton/reloadButton";
import Validator from "../../helpers/validator";
import {errorMessages} from "../../helpers/errorMessage";

interface TimetableState {
    timetableData: TimetableModel | undefined | null;
    days: TimetableDayWithRowsModel[];
    wharfsCount: number;
    numberOfWeeks: number;
    downloadPopupOpen: boolean;
    openCopySelectedWeekPopup: boolean;
    copyResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    yearCopyFromPopup: ValidatedValue<string>;
    weekCopyFromPopup: ValidatedValue<string>;
    yearToCopyPopup: ValidatedValue<string>;
    weekToCopyPopup: ValidatedValue<string>;
    numberOfWeeksResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentDateResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    timetableResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isWaiting: boolean;
    isLoading: boolean;
}

class Timetable extends React.Component <any, TimetableState> {
    static contextType = storesContext;

    longTime: Map<string, string[]> = new Map<string, string[]>();
    longTimeCanceled: Map<string, string[]> = new Map<string, string[]>();
    cellWidth = 190;
    // Number of columns outside port whrafs (ferry name and notes)
    fixedColumnsCount = 2;
    private year: ValidatedValue<string>;
    private weekNumber: ValidatedValue<string>;

    public constructor(props: any) {
        super(props);

        const notEmptyValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        this.year = new ValidatedValue("");
        this.weekNumber = new ValidatedValue("");

        this.state = {
            timetableData: undefined,
            days: [],
            wharfsCount: 0,
            numberOfWeeks: 0,
            downloadPopupOpen: false,
            openCopySelectedWeekPopup: false,
            copyResponse: undefined,
            currentDateResponse: undefined,
            numberOfWeeksResponse: undefined,
            yearCopyFromPopup: new ValidatedValue("", [notEmptyValidator]),
            weekCopyFromPopup: new ValidatedValue("", [notEmptyValidator]),
            yearToCopyPopup: new ValidatedValue("", [notEmptyValidator]),
            weekToCopyPopup: new ValidatedValue("", [notEmptyValidator]),
            timetableResponse: undefined,
            isWaiting: false,
            isLoading: false
        };
    }

    private get timetableData() {
        return this.state.timetableData;
    }

    public async componentDidMount() {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        const locationState = this.props.history.location.state;
        const currentData = await this.getCurrentDate();
        let year = currentData.year;
        let week = currentData.week;

        if (locationState && (locationState === "modify" || locationState === "cancel" || locationState === "remove")) {
            year = timetableStore.year ? timetableStore.year : year;
            week = timetableStore.week ? timetableStore.week : week;
        } else {
            timetableStore.clearYearAndWeek();
        }
        if (timetableStore.year === 0 || timetableStore.week === 0) this.setWeekAndYear(week, year);

        const timetableResponse = await TimetableService.getSelectedWeek(year, week);
        const timetableData = timetableResponse?.data;

        this.year.setValue(year?.toString());
        this.weekNumber.setValue(week?.toString());
        this.state.yearToCopyPopup.setValue(year?.toString());
        this.state.weekToCopyPopup.setValue(week?.toString());

        this.setState({
            timetableResponse,
            timetableData: timetableData || null,
            days: this.createModel(timetableData),
            wharfsCount: timetableData?.availableWharfs?.length || 0,
            numberOfWeeks: await this.getNumberOfWeeks(year)
        });

        if (timetableStore.editElement && timetableStore.editElement !== "") {
            Utils.scrollToElement(timetableStore.editElement);
            timetableStore.setEditElement("");
        }

        if (timetableStore.lastAddElement) {
            const data = timetableStore.lastAddElement.arrivalTime!.toLocaleString();

            for (const div of document.getElementsByClassName("day")) {
                if (div.innerHTML.search(data.split(",")[0]) != -1) {
                    div.id && Utils.scrollToElement(div.id);
                }
            }
            timetableStore.setLastAddElement(undefined);
        }

    }

    public render() {
        return (
            <div className="timetable-wrapper">
                {this.renderContent()}
            </div>
        );
    }

    private renderContent() {
        if (!Api.isSuccess(this.state.timetableResponse)) {
            return <HttpError
                cause={i18n.t("timetable.retrievalErrorSuffix")}
                response={this.state.timetableResponse}
            />;
        } else if (this.timetableData) {
            return this.renderTimetable();
        } else if (this.timetableData === undefined) {
            return <Loader/>;
        } else {
            return this.renderHeader();
        }
    }

    private renderTimetable() {
        return (
            <div className="timetable">
                {this.renderHeader()}
                <div className="timetable-content">
                    {this.renderWharfNames()}
                    {this.state.isLoading && <Loader />}
                    {!this.state.isLoading && (
                        <>
                            {this.renderBody()}
                        </>
                    )}
                </div>
                {this.renderDownloadPopup()}
                {this.state.openCopySelectedWeekPopup && this.renderCopyFromSelectedWeekPopup()}
            </div>
        );
    }

    private renderHeader() {
        return (
            <div className="header">
                <div className="filters">
                    {this.renderPeriodPresentDate()}
                    <div className="filter-actions">
                        <DropDown
                            label={i18n.t("timetable.headers.filters.year")}
                            items={this.yearsToDropDownMap}
                            validatedValue={this.year}
                            testId="filters-year"
                            onChange={this.onChangeWeek.bind(this, true)}
                            withoutSort
                        />
                        <DropDown
                            label={i18n.t("timetable.headers.filters.week")}
                            items={this.numberOfWeeksToDropDownMap}
                            validatedValue={this.weekNumber}
                            testId="filters-week"
                            onChange={this.onChangeWeek.bind(this, false)}
                            withoutSort
                        />
                        <Button
                            variant="text"
                            text={i18n.t("timetable.headers.filters.prevBtn")}
                            type="secondary"
                            onClick={this.onClickPrevWeek.bind(this)}
                            testId="filters-prev-btn"
                        />
                        <Button
                            variant="text"
                            text={i18n.t("timetable.headers.filters.nextBtn")}
                            type="secondary"
                            onClick={this.onClickNextWeek.bind(this)}
                            testId="filters-next-btn"
                        />
                    </div>
                </div>
                <div className="actions">
                    <Button
                        type="primary"
                        variant="outlined"
                        onClick={this.handleClickOpenCopyPopup.bind(this)}
                        text={i18n.t("timetable.actions.copyDataBtn")}
                        endIcon={<CopyWeekIcon/>}
                        testId="copy-week-btn"/>
                    <Button
                        type="primary"
                        variant="outlined"
                        onClick={this.onClickDownload.bind(this)}
                        text={i18n.t("timetable.actions.download.button")}
                        endIcon={<DownloadIcon/>}
                        testId="download-btn"/>
                    <NavLink to={routingAddresses.timetable.addAction} data-testid="add-action">
                        <Button type="primary" text={i18n.t("timetable.actions.addAction.button")}/>
                    </NavLink>
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="timetable-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderPeriodPresentDate() {
        if (!this.timetableData || !this.timetableData.startWeek || !this.timetableData.endWeek) {
            return null;
        }

        return <div className="period-presented-date">
            <InputLabel className="period-label">{i18n.t("timetable.periodDateLabel")}</InputLabel>
            <InputLabel>{DateUtils.getFormattedDate(this.timetableData.startWeek)} - {DateUtils.getFormattedDate(this.timetableData.endWeek)}
                <strong>({this.weekNumber.value})</strong>
            </InputLabel>
        </div>;
    }

    private renderCopyFromSelectedWeekPopup() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        return (
            <CopyWeekPopup
                timetableData={this.state.timetableData!}
                openCopySelectedWeekPopup={this.state.openCopySelectedWeekPopup}
                yearToCopy={this.state.yearToCopyPopup}
                weekToCopy={this.state.weekToCopyPopup}
                yearCopyFrom={this.state.yearCopyFromPopup}
                weekCopyFrom={this.state.weekCopyFromPopup}
                onCancel={() => {
                    this.state.yearCopyFromPopup.setValue("");
                    this.state.weekCopyFromPopup.setValue("");
                    this.state.yearCopyFromPopup.clearValidation();
                    this.state.weekCopyFromPopup.clearValidation();
                    this.state.yearToCopyPopup.setValue(timetableStore.year.toString());
                    this.state.weekToCopyPopup.setValue(timetableStore.week.toString());
                    this.setState({openCopySelectedWeekPopup: false, copyResponse: undefined});
                }}
                confirmCopyWeek={this.confirmCopySelectedData.bind(this)}
                yearDropdownMap={this.yearsToDropDownMap}
                weekDropDownMap={this.numberOfWeeksToDropDownMap}
                response={this.state.copyResponse}
                numberOfWeeks={this.state.numberOfWeeks}
                isWaiting={this.state.isWaiting}
            />
        );
    }

    private renderWharfNames() {
        const header = [];
        const headerStyle = {gridTemplateColumns: `repeat(${this.state.wharfsCount + this.fixedColumnsCount}, ${this.cellWidth}px)`};

        header.push(<Tile
            type="wharfLabel" data={i18n.t("timetable.wharf")} style={headerStyle} key="header-0"
            testId={"header-0"}/>);

        header.push(this.timetableData!.availableWharfs?.map((wharf, index) =>
            <Tile type="wharfName" data={wharf} key={`header-${index}`} testId={`header-${index}`}/>
        ));

        header.push(
            <Tile type="wharfLabel" data={i18n.t("timetable.outsideTPS")} key="header-notes" testId="header-notes" />
        );

        return (<div className="wharf-names" style={headerStyle}>{header}</div>);
    }

    private renderBody() {
        const rowStyle = {gridTemplateColumns: `repeat(${this.state.wharfsCount + this.fixedColumnsCount}, ${this.cellWidth}px`};
        const dayStyle = {width: `${(this.state.wharfsCount + this.fixedColumnsCount) * this.cellWidth}px`};

        return (
            this.state.days?.map((timetableDay, indexDay) => {
                return (
                    <div style={dayStyle} key={`day-${indexDay.toString()}`}>
                        <div
                            className="day" style={dayStyle}
                            data-testid={`day-${indexDay}`}
                            id={`day-${indexDay}`}
                        >
                            {timetableDay.dayName}
                        </div>
                        {timetableDay.rows?.map((row, indexRow) => {
                            return (
                                <Row
                                    class="grid-type"
                                    style={rowStyle}
                                    rowKey={`row-${indexDay}-${indexRow}`}
                                    key={`row-parent-${indexDay}-${indexRow}`}
                                >
                                    {row?.map((cell) => {
                                        return cell;
                                    })}
                                </Row>
                            );
                        })}
                    </div>
                );
            })
        );
    }

    private renderDownloadPopup() {
        if (this.state.downloadPopupOpen) {
            return <DownloadPopup
                onCancel={this.onCloseDownloadPopup.bind(this)}
                defaultFrom={this.state.timetableData?.startWeek}
                defaultTo={this.state.timetableData?.endWeek}
            />;
        }
        return <></>;
    }

    private get yearsToDropDownMap(): DropDownModel[] {
        return Utils.range(new Date().getFullYear() - 1, new Date().getFullYear() + 10).map(i => {
            return {id: `${i}`, name: `${i}`};
        });
    }

    private onClickDownload() {
        this.setState({downloadPopupOpen: true});
    }

    private onCloseDownloadPopup() {
        this.setState({downloadPopupOpen: false});
    }

    private handleClickOpenCopyPopup() {
        this.setState({
            openCopySelectedWeekPopup: true
        });
    }

    private setWeekAndYear(week: number, year: number) {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        timetableStore.setWeekAndYearStore(week, year);
    }

    private async onClickNextWeek() {
        this.setState({isLoading: true});
        const {
            weekNumber,
            year
        } = this.getNextWeek(Number.parseInt(this.weekNumber.value), this.state.numberOfWeeks, Number.parseInt(this.year.value));

        this.year.setValue(year.toString());
        this.weekNumber.setValue(weekNumber.toString());

        const timetableStore = this.context.timetableStore as ITimetableStore;
        if (this.year.value !== timetableStore.year.toString()) {
            this.setState({
                numberOfWeeks: await this.getNumberOfWeeks(Number.parseInt(this.year.value))
            });
        }

        this.setWeekAndYear(weekNumber, year);

        const timetableResponse = await TimetableService.getSelectedWeek(Number.parseInt(this.year.value), Number.parseInt(this.weekNumber.value));
        const timetableData = timetableResponse?.data || null;

        this.setCopyPopupData(Number.parseInt(this.year.value).toString(), Number.parseInt(this.weekNumber.value).toString());

        this.setState({
            timetableResponse,
            timetableData,
            days: this.createModel(timetableData),
            wharfsCount: timetableData?.availableWharfs?.length,
            isLoading: false
        });
    }

    private getNextWeek(weekNumber: number, numberOfWeeks: number, year: number): { weekNumber: number, year: number } {
        if (weekNumber === numberOfWeeks) {
            return {year: year + 1, weekNumber: 1};
        } else {
            return {year, weekNumber: weekNumber + 1};
        }
    }

    private async onClickPrevWeek() {
        this.setState({isLoading: true});
        if (this.weekNumber.value === "1") {
            const yearTmp = Number.parseInt(this.year.value) - 1;
            this.year.setValue(yearTmp.toString());
            this.setState({numberOfWeeks: await this.getNumberOfWeeks(Number.parseInt(this.year.value))});

            this.weekNumber.setValue(this.state.numberOfWeeks.toString());
        } else {
            this.weekNumber.setValue((Number.parseInt(this.weekNumber.value) - 1).toString());
        }
        this.setWeekAndYear(Number.parseInt(this.weekNumber.value), Number.parseInt(this.year.value));


        const timetableResponse = await TimetableService.getSelectedWeek(Number.parseInt(this.year.value), Number.parseInt(this.weekNumber.value));
        const timetableData = timetableResponse?.data || null;

        this.setCopyPopupData(Number.parseInt(this.year.value).toString(), Number.parseInt(this.weekNumber.value).toString());

        this.setState({
            timetableResponse,
            timetableData,
            days: this.createModel(timetableData),
            wharfsCount: timetableData?.availableWharfs?.length,
            isLoading: false
        });
    }

    private async onChangeWeek(getNumbersOfWeeks: boolean) {
        const numberOfWeeks = getNumbersOfWeeks ? await this.getNumberOfWeeks(Number.parseInt(this.year.value)) : this.state.numberOfWeeks;

        if (numberOfWeeks !== 0 && Number.parseInt(this.weekNumber.value) > numberOfWeeks) {
            this.weekNumber.setValue(numberOfWeeks.toString());
        }

        this.setWeekAndYear(Number.parseInt(this.weekNumber.value), Number.parseInt(this.year.value));

        const timetableResponse = await TimetableService.getSelectedWeek(Number.parseInt(this.year.value), Number.parseInt(this.weekNumber.value));
        const timetableData = timetableResponse?.data || null;

        this.setCopyPopupData(Number.parseInt(this.year.value).toString(), Number.parseInt(this.weekNumber.value).toString());

        this.setState({
            timetableResponse,
            timetableData,
            days: this.createModel(timetableData),
            wharfsCount: timetableData?.availableWharfs?.length,
            numberOfWeeks
        });
    }

    private setCopyPopupData(year: string, week: string) {
        this.state.yearToCopyPopup.setValue(year);
        this.state.weekToCopyPopup.setValue(week);
    }

    public get isValid() {
        this.state.yearCopyFromPopup.validate();
        this.state.weekCopyFromPopup.validate();
        this.state.yearToCopyPopup.validate();
        this.state.weekToCopyPopup.validate();

        return this.state.yearCopyFromPopup.isValid && this.state.weekCopyFromPopup.isValid &&
            this.state.yearToCopyPopup.isValid && this.state.weekToCopyPopup.isValid;
    }

    protected async confirmCopySelectedData() {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        this.setState({copyResponse: undefined, isWaiting: true});

        if (this.isValid) {
            const response = await TimetableService.copySelectedWeek({
                yearCopyFrom: Number.parseInt(this.state.yearCopyFromPopup.value),
                weekCopyFrom: Number.parseInt(this.state.weekCopyFromPopup.value),
                yearToCopy: Number.parseInt(this.state.yearToCopyPopup.value),
                weekToCopy: Number.parseInt(this.state.weekToCopyPopup.value)
            });

            this.setState({copyResponse: response});

            if (Api.isSuccess(response)) {
                this.state.yearCopyFromPopup.setValue("");
                this.state.weekCopyFromPopup.setValue("");

                this.setState({openCopySelectedWeekPopup: false});

                this.reload();

                this.state.yearToCopyPopup.setValue(timetableStore.year.toString());
                this.state.weekToCopyPopup.setValue(timetableStore.week.toString());
            }
        }

        Utils.scrollToFirstErrorMessage();
        this.setState({isWaiting: false});
    }

    private createModel(timetableData: TimetableModel) {
        const days: TimetableDayWithRowsModel[] = [];
        const availableWharfs = timetableData?.availableWharfs || [];

        timetableData?.timetableDays?.map((timetableDay, timetableDayIndex) => {
            const day: TimetableDayWithRowsModel = {
                dayName: DateUtils.getDateWithDayName(timetableDay.dayDate),
                rows: []
            };

            timetableDay.timetableActions?.map((action, actionIndex) => {
                day?.rows?.push(this.createCellsInRow(availableWharfs, action, timetableDayIndex, actionIndex));
            });

            days.push(day);
        });

        return days;
    }

    private createCellsInRow(availableWharfs: string[], action: SimpleTimetableActionModel, timetableDayIndex: number, actionIndex: number): JSX.Element[] {
        const cells: JSX.Element[] = [];
        const keyRow = `day-${timetableDayIndex}-row-${actionIndex}`;

        this.createEmptyCells(cells, availableWharfs, keyRow);
        this.createFerryNameCell(cells, action.ferryName, `day-${timetableDayIndex}-ferry-${actionIndex}`);
        this.createLongTimeCells(cells, availableWharfs, keyRow);
        this.createLongTimeCanceledCells(cells, availableWharfs, keyRow);
        this.createAllActionCells(cells, action, availableWharfs, keyRow);
        this.createNotesCell(cells, action.notes, `day-${timetableDayIndex}-note-${actionIndex}`);

        return cells;
    }

    private createEmptyCells(cells: JSX.Element[], availableWharfs: string[], key: string) {
        const cellCount = availableWharfs.length + 1;

        for (let i = 0; i < cellCount; i++) {
            cells.push(<Tile type="empty" key={`${key}-col-${i}`} testId={`${key}-col-${i}`}/>);
        }
    }

    private createFerryNameCell(cells: JSX.Element[], ferryName: string, key: string) {
        cells[0] = <Tile type="ferryName" data={ferryName} key={key} testId={key}/>;
    }

    private createNotesCell(cells: JSX.Element[], notes: string, key: string) {
        cells[cells.length+1] = <Tile type="notes" data={notes} key={key} testId={key}/>;
    }

    private createAllActionCells(cells: JSX.Element[], action: SimpleTimetableActionModel, availableWharfs: string[], key: string) {
        const actionCellIndex = availableWharfs.indexOf(action.wharfName) + 1;

        if (action.plannedStartTime && !action.plannedEndTime) {
            if (this.longTime.has(action.wharfName)) {
                const ferryInLongTimeTab = this.longTime.get(action.wharfName);
                this.longTime.set(action.wharfName, ferryInLongTimeTab!.concat(action.ferryName));
            } else {
                this.longTime.set(action.wharfName, [action.ferryName]);
            }

            if (action.canceled) {
                this.longTimeCanceled.set(action.wharfName, [action.ferryName]);
            }
            this.createActionCell(cells, action, actionCellIndex, true, key);
        } else if (!action.plannedStartTime && action.plannedEndTime) {
            if (this.longTime.has(action.wharfName)) {
                const ferryInLongTimeTab = this.longTime.get(action.wharfName);

                if (ferryInLongTimeTab!.length > 1) {
                    if (ferryInLongTimeTab!.find(x => x === action.ferryName)) {
                        const index = ferryInLongTimeTab!.findIndex(x => x === action.ferryName);
                        ferryInLongTimeTab!.splice(index, 1);
                    }
                } else {
                    this.longTime.delete(action.wharfName);
                }
            }

            if (action.canceled) {
                this.longTimeCanceled.delete(action.wharfName);
            }
            this.createActionCell(cells, action, actionCellIndex, true, key);
        } else {
            if (actionCellIndex > 0) {
                this.createActionCell(cells, action, actionCellIndex, false, key);
            }
        }
    }

    private createActionCell(cells: JSX.Element[], action: SimpleTimetableActionModel, actionCellIndex: number, isLongTime: boolean, key: string) {
        cells[actionCellIndex] = (
            <Tile
                type="action" data={action} isLongTime={isLongTime} key={`${key}-col-${actionCellIndex}`}
                testId={`${key}-col-${actionCellIndex}`} canUpdate={true}
                isCanceled={action.canceled}
            />
        );
    }

    private createLongTimeCells(cells: JSX.Element[], availableWharfs: string[], key: string) {
        const cellCount = availableWharfs.length + 1;

        for (let i = 0; i < cellCount; i++) {
            if (this.longTime.has(availableWharfs[i])) {
                cells[i + 1] = (
                    <Tile
                        type="empty" isLongTime={true} key={`long-time-${key}-${i}`}
                        testId={`long-time-${key}-col-${i}`}
                    />);
            }
        }
    }

    private createLongTimeCanceledCells(cells: JSX.Element[], availableWharfs: string[], key: string) {
        const cellCount = availableWharfs.length + 1;

        for (let i = 0; i < cellCount; i++) {
            if (this.longTimeCanceled.has(availableWharfs[i])) {
                cells[i + 1] = (
                    <Tile
                        type="empty" isLongTime={true} key={`long-time-${key}-${i}`}
                        testId={`long-time-${key}-col-${i}`}
                        isCanceled={true}
                    />);
            }
        }
    }

    private get numberOfWeeksToDropDownMap(): DropDownModel[] {
        return Utils.range(1, this.state.numberOfWeeks).map(i => {
            return {id: `${i}`, name: `${i}`};
        });
    }

    private async getNumberOfWeeks(year: number): Promise<number> {
        const numberOfWeeksResponse = await TimetableService.getNumberOfWeeks(year);
        this.setState({numberOfWeeksResponse});
        return numberOfWeeksResponse?.data?.numberOfWeeks || 0;
    }

    private async getCurrentDate(): Promise<WeekAndYearModel> {
        const currentDateResponse = await TimetableService.getCurrentDate();
        this.setState({currentDateResponse});
        return currentDateResponse?.data || null;
    }

    private async reload() {
        this.setState({timetableData: undefined});

        const timetableResponse = await TimetableService.getSelectedWeek(Number.parseInt(this.year.value), Number.parseInt(this.weekNumber.value));
        const timetableData = timetableResponse?.data || null;
        this.setState({
            timetableResponse,
            timetableData,
            days: this.createModel(timetableData),
            wharfsCount: timetableData?.availableWharfs?.length
        });
    }
}

export default withRouter(Timetable);
